/**
 * Service layer — Bookkeeping intake (docs/PRACTICE_FINANCE_PLAN.md Phase 0 + 1).
 * Talks to /api/finance-import only. Gated on the `finance-admin` module, admin-only for now.
 *
 * Nothing here writes to BQE Core. Committing to the ledger is a later slice, on purpose.
 */

async function authToken() {
  if (typeof window === 'undefined') return null
  for (let i = 0; i < 20; i++) {
    if (typeof window.__clerkGetToken === 'function') {
      try { const t = await window.__clerkGetToken(); if (t) return t } catch { /* not ready */ }
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return null
}

async function post(body) {
  const token = await authToken()
  const res = await fetch('/api/finance-import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || ('Request failed: ' + res.status))
  return json
}

/**
 * Plain-English labels for each classification bucket. One place, so the screen, any future
 * report and the docs all say the same words.
 */
export const BUCKET_LABEL = {
  auto:             'Coded automatically',
  low_confidence:   'Coded, but worth a look',
  unknown:          'Needs a decision',
  owner_draw:       'Owner draw',
  payroll_net:      'Payroll — net pay',
  payroll_tax:      'Payroll — tax & fees',
  payroll_401k:     'Payroll — 401(k) remittance',
  staff_other:      'Paid to staff, not payroll',
  historical_split: 'Coded several ways before',
  bank_fee:         'Bank fee',
  transfer:         'Transfer between your accounts',
  deposit:          'Money in (already in Core)',
  already_in_core:  'Already entered in Core — skip',
  owner_contribution: 'Owner contribution',
}

/** Order the buckets by how much attention each deserves, not alphabetically. */
export const BUCKET_ORDER = [
  'unknown', 'historical_split', 'staff_other', 'low_confidence',
  'owner_draw', 'payroll_net', 'payroll_tax', 'payroll_401k', 'auto', 'bank_fee', 'transfer', 'already_in_core', 'deposit',
]

/** Buckets that genuinely need a human before this batch can be called done. */
export const NEEDS_HUMAN = new Set(['unknown', 'historical_split', 'staff_other', 'low_confidence'])

/**
 * The three things a reader actually wants to know, in order: what is waiting on me, what will
 * happen without me, and what is deliberately being left alone.
 *
 * Before this, every bucket was one flat row of chips and the first one read "Needs a decision
 * 197" when 193 of those 197 were already decided — the count never came down, so the screen
 * showed no progress no matter how much work got done. Matt read that as the tool being
 * incomprehensible, which was a fair reading of a screen that was lying.
 *
 * The rule that fixes it: a NEEDS-YOU group counts what is still OUTSTANDING; the other groups
 * count what is there. Those are different questions and they were being answered with one number.
 */
export const BUCKET_GROUPS = [
  {
    key: 'needs-you',
    title: 'Waiting on you',
    blurb: 'Nothing here can be entered until you say which account it belongs to.',
    countPending: true,
    buckets: ['unknown', 'historical_split', 'staff_other'],
  },
  {
    key: 'will-post',
    title: 'Ready — no decision needed',
    blurb: 'Already coded. Worth a spot-check, but these are not holding anything up.',
    countPending: false,
    buckets: ['low_confidence', 'auto', 'owner_draw', 'payroll_net', 'payroll_tax',
              'payroll_401k', 'bank_fee'],
  },
  {
    key: 'excluded',
    title: 'Deliberately left out',
    blurb: 'Already in Core, or not an expense at all. These will never be entered again.',
    countPending: false,
    buckets: ['already_in_core', 'deposit', 'transfer', 'owner_contribution'],
  },
]

/** The real chart of accounts from Core — the picker's source of truth, not the memory. */
export function getChartOfAccounts()          { return post({ action: 'chart' }) }
export function listBatches()                 { return post({ action: 'list' }) }
export function getBatch(batchId, bucket)     { return post({ action: 'get', batchId, bucket: bucket || null }) }
export function seedMemory()                  { return post({ action: 'seedMemory' }) }
export function importBank(csv, filename, bankAccount) { return post({ action: 'processBank', csv, filename, bankAccount: bankAccount || null }) }
export function importPayroll(csv, filename)  { return post({ action: 'processPayroll', csv, filename }) }
export function discardBatch(batchId)         { return post({ action: 'discard', batchId }) }

export function updateRow(rowId, patch) {
  return post({ action: 'updateRow', rowId, ...patch })
}

/**
 * Re-run the proposals over an import with whatever the memory and payroll know NOW.
 * Decisions you have already made are left alone. This is what makes the upload order not matter:
 * load the bank file first and the payroll second, then re-check.
 */
export function reclassify(batchId) { return post({ action: 'reclassify', batchId }) }

/** Mark rows that already exist as checks in Core, so they are never posted twice. */
export function matchCore(batchId) { return post({ action: 'matchCore', batchId }) }

/**
 * Apply one account to every pending row for a vendor, and teach the memory.
 * This is the interaction the whole screen exists for: 59 unknown vendors cover 219 transactions,
 * and one of them (Anthropic) is 112 rows by itself.
 */
export function applyToVendor(batchId, vendor, accountCode, accountName, learn = true) {
  return post({ action: 'bulkApply', batchId, vendor, accountCode, accountName, learn })
}

/**
 * Project coding — the ITEMS route.
 *
 * A project cost does not take an account code. It names a PROJECT and an EXPENSE ITEM, and Core
 * derives the account from the item. So these rows ask for less, not more: two pickers instead of
 * a code, and the account follows.
 *
 * In Core the phase IS the project (`24-04-DC` is its own record), so there is one picker, not two.
 * There are ~1,500 of them, which is why this searches rather than offering a list.
 */
export function getExpenseItems()            { return post({ action: 'expenseItems' }) }
export function searchCoreProjects(q)        { return post({ action: 'coreProjects', q }) }

export function setProjectCoding(rowId, projectId, projectName, itemId, itemName, billable = true, memo) {
  return post({ action: 'setProjectCoding', rowId, projectId, projectName, itemId, itemName, billable, memo })
}

/** Take a row back off the project route, so it can be coded to a plain account again. */
export function clearProjectCoding(rowId)    { return post({ action: 'setProjectCoding', rowId }) }
/** One charge split across projects (a print shop's monthly charge). No lines clears it. */
export function setProjectSplit(rowId, lines) { return post({ action: 'setProjectSplit', rowId, lines }) }
/** Add a vendor to Core by name (the newest vendor's term and group). */
export function addCoreVendor(name, pattern) { return post({ action: 'addCoreVendor', name, pattern, apply: true }) }
/** Post a project cost even though its phase's allowed list may refuse it. */
export function phaseListOk(rowId) { return post({ action: 'phaseListOk', rowId }) }

/**
 * Approved COMPANY-PAID expenses that match a bank charge (same amount, within 10 days), per row,
 * and the expense each row is already linked to (2026-10-28). Linking fills the row's project, item
 * and billable from the expense; posting the check then marks the expense Recorded in BQE Core.
 */
export function getExpenseMatches(batchId)   { return post({ action: 'expenseMatches', batchId }) }
export function linkExpenseToRow(rowId, expenseId) { return post({ action: 'linkExpense', rowId, expenseId }) }
export function unlinkExpenseFromRow(rowId)  { return post({ action: 'linkExpense', rowId }) }

/** A Zelle or check to a team member that pays back their open Reimb bills (2026-10-28). */
export function getReimbMatches(batchId)     { return post({ action: 'reimbMatches', batchId }) }
export function linkReimbToRow(rowId, billIds) { return post({ action: 'linkReimb', rowId, billIds }) }
export function unlinkReimbFromRow(rowId)    { return post({ action: 'linkReimb', rowId }) }

/** Accounts that mean "this is a project cost" — mirrors PROJECT_ACCOUNTS in api/_financeCommit.js. */
/* ── Posting and reconciling ───────────────────────────────────────────────
   These four actions existed on the server from the first day and had no client at all: every
   posting run so far was driven from a browser console. That is what made the tool need Claude in
   the room, and it is the single thing that stopped it being the firm's own. */

/**
 * DRY RUN. Recomputes what every outgoing row in the window would become. Writes nothing.
 * Always run this first — it is the only chance to see a wrong account before it is a ledger entry.
 */
export function planCommit(batchId, from, to) {
  return post({ action: 'commitPlan', batchId, from, to })
}

/**
 * THE WRITE. Creates real checks in BQE Core, which has no reversal we trust.
 *
 * The server recomputes the decision from scratch, so nothing sent from here decides an account or
 * an amount; it refuses any row already carrying a Core id, which is what makes pressing the button
 * twice harmless; and it caps the batch, so a mistake costs a handful of rows rather than a month.
 */
export function commitRows(rowIds) {
  return post({ action: 'commitRows', rowIds })
}

/**
 * Did the window land intact? Fetches every Core check linked to a row in it and compares each
 * against the sum of ALL rows linked to it. Counting rows cannot find a double-booking — a
 * duplicate and an omission both leave the count looking reasonable.
 */
export function reconcileWindow(batchId, from, to) {
  return post({ action: 'reconcileWindow', batchId, from, to })
}

/** Does Core's net change over the period equal the bank's? */
export function reconcilePeriod(batchId, from, to) {
  return post({ action: 'reconcilePeriod', batchId, from, to })
}

export const PROJECT_ACCOUNTS = new Set(['5000', '5100', '5200', '5300'])

/* ── Connecting a bank description to a payee Core already has ──────────────
   The bank says "slack t024dcuhdtm slack com"; Core has "Slack Technologies, LLC". Same company.
   Until 2026-09-16 recording that fact was a server action with no screen calling it, so 179 held
   transactions could only be cleared with Claude in the room. Now it is a control on the held row. */

/** Every vendor in the import that Core does not recognise, biggest first — Review's second queue. */
export function getVendorStatus(batchId) { return post({ action: 'vendorStatus', batchId }) }

/** Vendors and employees Core already has, nearest to what was typed. */
export function searchCorePayees(q) { return post({ action: 'payees', q }) }

/**
 * Teach the memory that this bank description IS that Core payee. Reversible (a row in Cortex,
 * nothing in Core), and the writer stops holding every transaction that carries the description.
 */
export function linkPayee(pattern, payee, note) {
  return post({
    action: 'linkPayee', pattern,
    corePayeeId: payee.id, payeeName: payee.name, payeeType: payee.type, note: note || null,
  })
}

/* ── Undeposited funds ─────────────────────────────────────────────────────
   Client payments recorded as received and never deposited. Nothing about the P&L, the invoices or
   the tax return is affected — the money is in the wrong asset account, and a deposit is a POINTER
   at payments that already exist rather than a new transaction.

   Real size: 4 payments, $6,395, Aug-Dec 2025. NOT the $56,972.44 quoted before — that came from a
   filter that counted payments already deposited. */

/** What is still sitting in Undeposited Funds over a period. */
export function listUndeposited(from, to)    { return post({ action: 'undeposited', from, to }) }

/**
 * Step one of money-in: link each bank credit to the deposit Core already holds for it, and name
 * the credits that were never client money (transfers in, owner contributions, processor payouts).
 * apply=false reports; apply=true writes the links into Cortex. Nothing is written to Core.
 */
export function matchCoreDeposits(batchId, apply) {
  return post({ action: 'matchCoreDeposits', batchId, apply: !!apply })
}

/** DRY RUN. Matches the bank's own credits to the payments that make them up. Writes nothing. */
export function planDeposits(batchId, from, to) {
  return post({ action: 'planDeposits', batchId, from, to })
}

/** THE WRITE. Creates deposits in Core, clearing the payments they point at. */
export function commitDeposits(rowIds)       { return post({ action: 'commitDeposits', rowIds }) }

// ── the monthly close (2026-10-28, docs/BOOKKEEPING.md "The monthly close") ──
export function getCloseStatus()               { return post({ action: 'closeStatus' }) }
export function confirmNoActivity(bank, after) { return post({ action: 'closeConfirm', bank, noActivityAfter: after }) }
export function getClosePlan()                 { return post({ action: 'closePlan' }) }
export function getCloseTie()                  { return post({ action: 'closeTie' }) }
export function getCloseRegister()             { return post({ action: 'closeRegister' }) }
export function getClosePayments()             { return post({ action: 'closePayments' }) }
export function getCloseReimbursables()        { return post({ action: 'closeReimbursables' }) }
export function markExpenseNotBillable(id)    { return post({ action: 'expenseNotBillable', id }) }
export function getSetAsidePreview()           { return post({ action: 'setAsidePreview' }) }
export function getReserves()                  { return post({ action: 'reserves' }) }
export function moveReserve(direction, bucket, amount, date, note) { return post({ action: 'reserveMove', direction, bucket, amount, date, note }) }
export function markInvoice(invoiceId, mark, note) { return post({ action: 'markInvoice', invoiceId, mark, note }) }
export function writeOffInvoice(invoiceId, amount, apply) { return post({ action: 'writeOff', invoiceId, amount, apply: !!apply }) }
export function finishClose(explanation, skipBuckets) { return post({ action: 'closeFinish', explanation: explanation || '', skipBuckets: skipBuckets || [] }) }
export function getClosePackage(month)         { return post({ action: 'closePackage', month }) }
export function saveSetAsideTargets(value)     { return post({ action: 'setAsideTargets', value }) }
/** What a bank credit with no client payment is: { kind: 'lines', code, label } | { kind: 'transfer' } | { kind: null }. */
export function setMoneyIn(rowId, choice)      { return post({ action: 'setMoneyIn', rowId, ...choice }) }
export function writeRetainerPayment({ rowId, clientId, projectId, invoiceId, method }) {
  return post({ action: 'writeRetainerPayment', rowId, clientId, projectId, invoiceId, method, apply: true })
}
export function writeDepositWithLines(rowId, lines, memo, paymentIds) {
  return post({ action: 'writeDepositWithLines', rowId, lines, memo, paymentIds: paymentIds || [], apply: true })
}
export function writeFundTransfer(rowId, mirrorRowId) {
  return post({ action: 'writeFundTransfer', rowId, mirrorRowId: mirrorRowId || null, apply: true })
}
export function getCloseGate()                 { return post({ action: 'closeGate' }) }
// The statements step: the bank's own PDF + typed balances, beside the downloads and Core.
export function getCloseStatements()           { return post({ action: 'closeStatements' }) }
export function saveStatement(bank, fields)    { return post({ action: 'statementSave', bank, ...fields }) }
export async function uploadStatement(bank, file) {
  const { signedUrl, storagePath } = await post({ action: 'statementUploadUrl', bank, filename: file.name })
  const r = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/pdf' }, body: file })
  if (!r.ok) throw new Error('Upload failed (' + r.status + ')')
  return saveStatement(bank, { storagePath, filename: file.name })
}
export function snoozeCloseGate()              { return post({ action: 'closeSnooze' }) }
/** Describe a transaction; the firm's past payments answer first, the AI only when none match. */
export function askAccount(text, ctx) { return post({ action: 'askAccount', text, vendor: ctx.vendor || null, description: ctx.description || null, amount: ctx.amount || null }) }

/* ── The CPA reports center, Bookkeeping side (docs/CPA_REPORTS.md) ── */
/** -> { access: [...], files: [...], events: [...] } */
export function getCpaCenter() { return post({ action: 'cpaCenter' }) }
/** op: add | newPin | newLink | off | on | removeFile | openFile; returns the center (openFile: { url }). */
export function cpaAction(op, fields = {}) { return post({ action: 'cpaAction', op, ...fields }) }
/** Upload one report into the center. meta: { title, kind, year, quarter?, month? } */
export async function addCpaFile(file, meta) {
  const { signedUrl, storagePath } = await post({ action: 'cpaAction', op: 'uploadUrl', filename: file.name })
  const put = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file })
  if (!put.ok) throw new Error('Upload failed (' + put.status + ')')
  return post({ action: 'cpaAction', op: 'addFile', storagePath, filename: file.name, size: file.size, ...meta })
}
/** Quarters and years that have ended (through `through`, a date) still missing a CPA report. -> { owed } */
export function getCpaOwed(through) { return post({ action: 'cpaOwed', through }) }
